import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';

import { putApplicability } from 'features/HorizonScanning/api';
import {
	IApplicabilityDropdownProps,
	IStatusDetails,
} from 'features/HorizonScanning/api/type-def';
import {
	HORIZON_APPLICABILITY_STATUS,
	HORIZON_PROGRESS_BAR_ELEMENTS,
	HORIZON_APPLICABILITY_DROPDOWN_LABEL,
} from 'features/HorizonScanning/HorizonScanConstants';

import styles from './ApplicabilityDropdown.module.scss';

const { labelColor, borderColor, backgroundColor, labelSpacing } = styles;

const { COMPLETE } = HORIZON_PROGRESS_BAR_ELEMENTS;
const { APPLICABILITY, IDENTIFY_CHANGE } = HORIZON_APPLICABILITY_DROPDOWN_LABEL;
const {
	APPLICABLE,
	NOTAPPLICABLE,
	POTENTIALLY_ACTIONABLE,
	INFORMATIONAL,
	IGNORE,
} = HORIZON_APPLICABILITY_STATUS;
const { OK } = HTTP_STATUS_CODES;

const ApplicabilityDropdown = (props: IApplicabilityDropdownProps) => {
	const {
		openOverride,
		data,
		stepperStatus,
		openCommentsSection,
		makeAPICall,
		scrollToCommentsSection,
	} = props;

	const dispatch = useDispatch();
	const [shrink, setShrink] = useState(false);
	const [applicableValue, setApplicableValue] = useState<string>(
		data?.applicableStatus ?? '',
	);
	const [applicableSecondValue, setApplicableSecondValue] = useState<string>(
		data?.applicableSecondaryStatus ?? '',
	);
	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const loggedInUser: any = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const onApplicableDropdownValueChange = (
		event: SelectChangeEvent<string>,
	) => {
		const { value } = event.target;
		if (value === APPLICABLE) {
			openCommentsSection(false);
		} else {
			openCommentsSection(true);
			scrollToCommentsSection();
		}
		setApplicableSecondValue('');
		setApplicableValue(value);
	};
	const onApplicableSecondDropdownValueChange = (
		event: SelectChangeEvent<string>,
	) => {
		const { value } = event.target;
		if (applicableValue === APPLICABLE && value) {
			const statusData: IStatusDetails = {
				applicableStatus: applicableValue,
				applicableSecondaryStatus: value,
				comments: '',
			};
			putStatusResponse(statusData, data?.id);
		}
		setApplicableSecondValue(value);
	};
	const handleFocus = () => {
		setShrink(true);
	};

	const handleToastHide = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);
	const putStatusResponse = async (statusData: IStatusDetails, id: string) => {
		if (!statusData?.applicableStatus) return;
		const responseFromApi = await putApplicability(statusData, id);

		if (responseFromApi?.statusCode === OK) {
			makeAPICall(true);
		} else {
			makeAPICall(false);
		}
	};

	const selectedStyles = {
		select: {
			'&.Mui-disabled .MuiSelect-root': {
				borderBottom: 'none' /* Remove the border-bottom */,
				backgroundColor,
			},
			'&.Mui-disabled': {
				borderColor, // change this to your desired color
			},
			'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
				bordercolor: borderColor,
			},
		},
	};

	useEffect(() => {
		const handleDataUpdate = () => {
			const status = data?.applicableStatus?.trim();
			const isApplicable = status === APPLICABLE;
			setApplicableValue(isApplicable ? APPLICABLE : status || '');
			if (isApplicable) {
				setApplicableSecondValue(data.applicableSecondaryStatus?.trim() || '');
			}
			openCommentsSection(status === NOTAPPLICABLE);
		};
		handleDataUpdate();
	}, [data]);
	const checkDisabled =
		data?.impacted ||
		stepperStatus === COMPLETE ||
		data?.assignedUserId === null ||
		data?.assignedUserId !== loggedInUser?.userId;
	const shrinkFlag =
		shrink ||
		data?.applicableStatus === APPLICABLE ||
		data?.applicableStatus === NOTAPPLICABLE;
	return (
		<div
			className={styles['applicability-div']}
			data-testId="applicability-dropdown-section"
		>
			<Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
				<FormControl className={styles['applicability-formcontrol']}>
					<InputLabel
						className={styles['applicability-label']}
						data-testid="impact-assessment-label"
						sx={{
							'&.MuiInputLabel-root[data-shrink="true"]': {
								color: labelColor,
								marginTop: labelSpacing,
							},
						}}
						shrink={shrinkFlag}
					>
						{APPLICABILITY}
					</InputLabel>
					{checkDisabled ? (
						<p
							className={styles['selected-value']}
							data-testid="applicable-status-text"
						>
							{data?.applicableStatus}
						</p>
					) : (
						<Select
							open={openOverride}
							disabled={checkDisabled}
							className={styles['applicability-item']}
							onChange={(e) => onApplicableDropdownValueChange(e)}
							inputProps={{
								'data-testid': 'applicable-dropdown',
							}}
							IconComponent={ExpandMoreRoundedIcon}
							value={applicableValue}
							sx={selectedStyles.select}
							onFocus={handleFocus}
						>
							<MenuItem value={APPLICABLE} data-testid="Applicable">
								{APPLICABLE}
							</MenuItem>
							<MenuItem value={NOTAPPLICABLE} data-testid="Not-Applicable">
								{NOTAPPLICABLE}
							</MenuItem>
						</Select>
					)}
				</FormControl>
				{applicableValue === APPLICABLE && (
					<FormControl className={styles['applicability-formcontrol2']}>
						<InputLabel
							className={styles['applicability-label']}
							data-testid="impact-assessment-label"
							sx={{
								'&.MuiInputLabel-root[data-shrink="true"]': {
									color: labelColor,
									marginTop: labelSpacing,
								},
							}}
							shrink={shrinkFlag}
						>
							{IDENTIFY_CHANGE}
						</InputLabel>
						{checkDisabled ? (
							<p
								className={styles['selected-value']}
								data-testid="applicable-status-second-text"
							>
								{applicableSecondValue}
							</p>
						) : (
							<Select
								open={openOverride}
								disabled={checkDisabled}
								className={styles['applicability-item']}
								onChange={(e) => onApplicableSecondDropdownValueChange(e)}
								inputProps={{
									'data-testid': 'applicable-second-dropdown',
								}}
								IconComponent={ExpandMoreRoundedIcon}
								value={applicableSecondValue}
								sx={selectedStyles.select}
								onFocus={handleFocus}
							>
								<MenuItem
									value={POTENTIALLY_ACTIONABLE}
									data-testid="Potentially_Actionable"
								>
									{POTENTIALLY_ACTIONABLE}
								</MenuItem>
								<MenuItem value={INFORMATIONAL} data-testid="Informational">
									{INFORMATIONAL}
								</MenuItem>
								<MenuItem value={IGNORE} data-testid="Ignore">
									{IGNORE}
								</MenuItem>
							</Select>
						)}
					</FormControl>
				)}
			</Stack>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToastHide}
				vertical="top"
				horizontal="right"
			/>
		</div>
	);
};

export default ApplicabilityDropdown;
